import { Action } from '@reduxjs/toolkit'
import prop from 'lodash/fp/prop'
import axios from 'axios'

import { AppDispatch } from 'app/store'

export * from './selectors'
export * from './consts'
export * from './slice'
export * from './types'

const dataTransformer = prop('data')
export const defaultServerErrors: FetchErrors = [
  { detail: 'Something went wrong with your request' },
]

interface FetchDataParams {
  dispatch: AppDispatch
  actions?: FetchActions
  asyncFunc: () => void
  transformResponse?: (res: any) => any
  suppressErrors?: boolean
  skipSaveToStore?: boolean
}

interface FetchActions {
  init: () => Action
  succeed: (res: any) => Action
  failure: (err: any) => Action
}

interface ApiError {
  detail: string
  code?: string
}

export type FetchErrors = ApiError[]

export async function fetchDataThunk({
  dispatch,
  actions,
  asyncFunc,
  transformResponse = dataTransformer,
  suppressErrors,
}: FetchDataParams) {
  try {
    if (actions?.init) dispatch(actions.init())
    const response = await asyncFunc()
    const transformedResponse = transformResponse(response)

    if (actions?.succeed) dispatch(actions.succeed(transformedResponse))

    return transformedResponse
  } catch (err: unknown) {
    let errors: FetchErrors = defaultServerErrors

    if (axios.isAxiosError(err)) {
      const validationErrorMessage =
        err?.response?.data?.errorMessage || err?.response?.data?.error?.message
      const validationErrors = validationErrorMessage && [
        { detail: validationErrorMessage },
      ]

      const apiErrors = err?.response?.data?.errors
      errors = validationErrors ?? apiErrors ?? defaultServerErrors
    }

    if (actions?.failure) dispatch(actions.failure(errors))

    if (!suppressErrors) {
      throw err
    }
  }
}

export async function fetchEdgeDataThunk(params: FetchDataParams) {
  return fetchDataThunk({
    ...params,
    transformResponse: (props) => {
      const { payload, error } = prop('data', props)
      if (error) throw error
      return payload
    },
  })
}

export async function fetchHomeDataThunk(params: FetchDataParams) {
  return fetchDataThunk({
    ...params,
    transformResponse: (props) => {
      const { data, error } = prop('data', props)
      if (error) throw error
      return data
    },
  })
}

export async function fetchConfigDataThunk(params: FetchDataParams) {
  return fetchDataThunk({
    ...params,
    transformResponse: (props) => {
      const { data, error } = props
      if (error) throw error
      return data
    },
  })
}
